import { DialogOptions } from '../../../models/modal.model'
import {
  GenericNotificationCode,
  NotificationOptions,
  parseGenericNotification,
} from '../../../models/notification.model'
import { User } from '../../users'

export type PackingListNotificationCode =
  | 'MORE_THAN_ONE_PACKAGE'
  | 'TOTE_NOT_FOUND'
  | 'ORDER_NOT_FOUND'
  | 'ORDER_NOT_SELECTED'
  | 'ORDER_UPDATED'
  | 'ORDER_PRINTED'
  | 'MISSING_ORDER_DATA'
  | 'MISSING_PACKING_DATA'
  | 'TOTE_EMPTY'
  | 'PACKAGE_NOT_FOUND'
  | 'PACKAGE_DISABLED'
  | 'PACKAGES_MISSING'
  | 'PACKAGES_NOT_FOUND'
  | 'PICKING_WITHOUT_TOTES'
  | 'PICKING_NOT_PACKABLE'
  | 'STATION_NOT_SELECTED'
  | 'PRODUCT_NOT_FOUND'
  | 'PRODUCT_PACK_NOT_CONTAINED'
  | 'PRODUCT_ORDER_NOT_CONTAINED'
  | 'PRODUCT_ALREADY_PACKED'
  | 'TOO_HEAVY'
  | GenericNotificationCode

export type PackingListModalCode =
  | 'PACKED_BY_ANOTHER_USER'
  | 'SET_PACKAGES_LOCATION'
  | 'PACKAGE_NOT_INCLUDED'
  | 'PACKAGES_INVERTED'
  | 'WEIGHT_CANNOT_BE_ZERO'

/**
 * Parse goods return code to a notification
 * @param code - the goods return notification code
 * @returns the notification options
 */
export function parsePackingListNotification(
  code?: PackingListNotificationCode,
): NotificationOptions {
  switch (code) {
    case 'MORE_THAN_ONE_PACKAGE':
      return {
        message: 'There is more than one package. Change carrier',
        color: 'error',
      }
    case 'TOTE_NOT_FOUND':
      return {
        message: 'Tote not found',
        color: 'error',
      }
    case 'ORDER_UPDATED':
      return {
        message: 'Order successfully updated',
        color: 'success',
      }
    case 'ORDER_PRINTED':
      return {
        message: 'Order label printed',
        color: 'success',
      }
    case 'MISSING_ORDER_DATA':
      return {
        message: 'Missing order data',
        color: 'error',
      }
    case 'MISSING_PACKING_DATA':
      return { message: 'Missing packing data', color: 'error' }
    case 'ORDER_NOT_SELECTED':
      return {
        message: 'Order not selected',
        color: 'warn',
      }
    case 'ORDER_NOT_FOUND':
      return {
        message: 'Order not found',
        color: 'error',
      }
    case 'TOTE_EMPTY':
      return {
        message: 'Tote without picking-lists',
        color: 'error',
      }
    case 'PACKAGE_NOT_FOUND':
      return { message: 'Package not found', color: 'error' }
    case 'PACKAGE_DISABLED':
      return {
        message: 'Package disabled',
        color: 'warn',
      }
    case 'PACKAGES_MISSING':
      return {
        message: 'Packages missing. You need to add at least one package',
        color: 'error',
      }
    case 'PACKAGES_NOT_FOUND':
      return { message: 'Packages not found', color: 'error' }
    case 'PICKING_WITHOUT_TOTES':
      return {
        message: 'Picking without totes',
        color: 'error',
      }
    case 'PICKING_NOT_PACKABLE':
      return {
        message: 'Picking orders not packable',
        color: 'error',
      }
    case 'STATION_NOT_SELECTED':
      return {
        message: 'Station not selected',
        color: 'error',
      }
    case 'PRODUCT_NOT_FOUND':
      return {
        title: 'Product not found',
        message: 'No products found with this code.',
        color: 'error',
      }
    case 'PRODUCT_ORDER_NOT_CONTAINED':
      return {
        message: 'Product not contained in the current order',
        color: 'error',
      }
    case 'PRODUCT_PACK_NOT_CONTAINED':
      return {
        message: 'Product not contained in the current packing-list',
        color: 'error',
      }
    case 'PRODUCT_ALREADY_PACKED':
      return {
        message: 'Product already packed',
        color: 'error',
        bigger: true,
      }
    case 'TOO_HEAVY':
      return {
        message: 'The weight is more than 26kg. Change carrier',
        color: 'error',
      }
    default:
      return parseGenericNotification(code)
  }
}

/**
 * Parse goods return code to an modal
 * @param code - the goods return modal code
 * @param packingList - the goods return object
 * @returns the modal settings
 */
export function parsePackingListModal(
  code: PackingListModalCode,
  data?: {
    pickingListName?: string
    user?: User
    packCode?: string
    packWeight?: string
  },
): DialogOptions {
  switch (code) {
    case 'PACKED_BY_ANOTHER_USER': {
      const messageParams = []

      if (data?.pickingListName) {
        messageParams.push(data.pickingListName)
      }

      if (data?.user) {
        messageParams.push(`${data.user.name} ${data.user.surname}`)
      } else {
        messageParams.push(`User not found`)
      }

      return {
        title: 'Picking-list already started',
        message:
          'Picking-list <strong>%s</strong> already started by <strong>%s</strong>. Proceed with packing?',
        messageParams,
        buttons: ['Cancel', 'Continue'],
      }
    }
    case 'SET_PACKAGES_LOCATION': {
      return {
        title: 'Set packages location',
        message:
          "You're about to start packing but you have not selected the packages location. Go back to the previous page and select it",
        buttons: ['Cancel', 'Continue'],
      }
    }
    case 'PACKAGE_NOT_INCLUDED': {
      return {
        title: 'Scanned package error',
        message:
          'You scanned the package of a product not included in this order (code %s)',
        messageParams: [data?.packCode ?? ''],
        buttons: ['Ok'],
      }
    }
    case 'PACKAGES_INVERTED':
      return {
        title: 'Check packages',
        message: 'Possible package inversion. Check again (%s kg)',
        messageParams: [data?.packWeight ?? ''],
        buttons: ['Continue', 'Cancel'],
      }
    case 'WEIGHT_CANNOT_BE_ZERO':
      return {
        title: 'Packages weight error',
        message: 'Selected packages weight cannot be 0. Weight the packages',
        buttons: ['Ok'],
      }
    default:
      return {}
  }
}
